import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// Create axios instance with auth interceptor
const api = axios.create({
  baseURL: `${API_URL}/ai-reports`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token might be expired, redirect to login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface APIResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface GenerateReportRequest {
  organization_type: 'company' | 'team' | 'department';
  organization_id: string;
  date_from: string;
  date_to: string;
  notes?: string;
}

export interface AIReport {
  id: string;
  organization_name: string;
  organization_type: string;
  date_from: string;
  date_to: string;
  created_at: string;
  created_by_name: string;
  risk_count: number;
  is_complete: boolean;
  ai_provider: string;
  error_message?: string;
}

export interface AIReportAttachment {
  id: string;
  file_url: string;
  download_url: string;
  file_name: string;
  file_size: number;
  uploaded_by_name: string;
  uploaded_at: string;
}

export interface AIReportDetail extends AIReport {
  company?: any;
  team?: any;
  department?: any;
  created_by: {
    id: string;
    email: string;
    full_name: string;
  };
  ai_overview: string;
  high_inherent_count: number;
  high_residual_count: number;
  inherent_matrix_data: {
    data: number[][];
    type: string;
  };
  residual_matrix_data: {
    data: number[][];
    type: string;
  };
  ai_model: string;
  generation_time: number;
  tokens_used?: number;
  html_content?: string;
  included_risks: any[];
  export_formats: {
    format: string;
    name: string;
    available: boolean;
  }[];
  notes?: string;
  attachments?: AIReportAttachment[];
}

class AIReportsService {
  async generateReport(data: GenerateReportRequest): Promise<APIResponse<AIReportDetail>> {
    try {
      const response = await api.post('/generate/', data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to generate report',
      };
    }
  }

  async getReports(params?: {
    organization_type?: string;
    organization_id?: string;
  }): Promise<APIResponse<AIReport[]>> {
    try {
      const response = await api.get('/reports/', { params });
      return {
        success: true,
        data: response.data.results || response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch reports',
      };
    }
  }

  async getReport(id: string): Promise<APIResponse<AIReportDetail>> {
    try {
      const response = await api.get(`/reports/${id}/`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch report',
      };
    }
  }

  async getReportStatus(id: string): Promise<APIResponse<{
    is_complete: boolean;
    error_message?: string;
    generation_time?: number;
  }>> {
    try {
      const response = await api.get(`/reports/${id}/status/`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch report status',
      };
    }
  }

  getDownloadUrl(reportId: string, format: 'html' | 'pdf' | 'docx'): string {
    return `/api/ai-reports/reports/${reportId}/download/${format}/`;
  }

  async downloadReport(reportId: string, format: 'html' | 'pdf' | 'docx'): Promise<void> {
    try {
      const response = await api.get(`/reports/${reportId}/download/${format}/`, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename from response headers or default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `risk_report_${reportId}.${format}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      throw new Error('Failed to download report');
    }
  }

  async deleteReport(reportId: string): Promise<APIResponse<void>> {
    try {
      await api.delete(`/reports/${reportId}/delete/`);
      return {
        success: true,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete report',
      };
    }
  }

  async uploadAttachment(reportId: string, file: File): Promise<APIResponse<AIReportAttachment>> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/reports/${reportId}/attachments/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.file?.[0] || 'Failed to upload attachment',
      };
    }
  }

  async deleteAttachment(reportId: string, attachmentId: string): Promise<APIResponse<void>> {
    try {
      await api.delete(`/reports/${reportId}/attachments/${attachmentId}/`);
      return {
        success: true,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete attachment',
      };
    }
  }

  async downloadAttachment(reportId: string, attachmentId: string, fileName: string): Promise<void> {
    try {
      const response = await api.get(`/reports/${reportId}/attachments/${attachmentId}/download/`, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      throw new Error('Failed to download attachment');
    }
  }
}

export default new AIReportsService();